"use client";

import { HomeQueryParam, TeamModalUrlMode } from "@/constants/navigation.enum";

const HOME_PATH = "/";

function viewTeamSearch(teamId: string): string {
  const params = new URLSearchParams();
  params.set(HomeQueryParam.ViewTeam, teamId);
  return params.toString();
}

/**
 * The landing PATH that reopens a team — the form a router push wants.
 * `router.push` navigates within the app; handing it an absolute URL makes it
 * treat the destination as external, so the two forms are separate exports of
 * the same owner rather than one absolute builder used everywhere.
 */
export function buildViewTeamPath(teamId: string): string {
  return `${HOME_PATH}?${viewTeamSearch(teamId)}`;
}

/** The landing PATH that reopens a team with deploy invoked
 *  (`?viewTeam=<id>&mode=deploy`) — the form a router push wants. */
export function buildDeployTeamPath(teamId: string): string {
  const params = new URLSearchParams();
  params.set(HomeQueryParam.ViewTeam, teamId);
  params.set(HomeQueryParam.TeamModalMode, TeamModalUrlMode.Deploy);
  return `${HOME_PATH}?${params.toString()}`;
}

/** The absolute landing URL for a team — the form a share sheet or clipboard
 *  needs, since a recipient has no origin to resolve a path against. */
export function buildViewTeamUrl(teamId: string): string {
  return new URL(buildViewTeamPath(teamId), window.location.origin).toString();
}

/**
 * The one writer of the landing `?viewTeam=` param — the hero's details
 * intents, the standalone team overlay AND the teams-gallery deck all attach
 * an open team through this, so a refresh restores it via the landing's
 * `useViewTeamUrlRestore`.
 */
export function setViewTeamUrl(teamId: string | null): void {
  const params = new URLSearchParams(window.location.search);

  if (!teamId) {
    params.delete(HomeQueryParam.ViewTeam);
  } else {
    params.set(HomeQueryParam.ViewTeam, teamId);
  }

  const nextUrl = params.toString()
    ? `${window.location.pathname}?${params.toString()}`
    : window.location.pathname;
  window.history.replaceState(null, "", nextUrl);
}

/** The currently-attached `?viewTeam=` value (null when absent). */
export function getViewTeamUrl(): string | null {
  return new URLSearchParams(window.location.search).get(
    HomeQueryParam.ViewTeam,
  );
}

/** Resolve a raw `mode` param value to the grammar's closed set — an unknown
 *  or absent value reads as null (the caller's default: the view state). */
export function parseTeamModalUrlMode(
  rawMode: string | null,
): TeamModalUrlMode | null {
  if (rawMode == null) return null;
  const knownModes = Object.values(TeamModalUrlMode) as string[];
  return knownModes.includes(rawMode) ? (rawMode as TeamModalUrlMode) : null;
}

/**
 * The one writer of the full team-modal URL grammar: `?viewTeam=<id>` plus
 * `mode=deploy` while the modal sits in deploy — both params in ONE history
 * replacement so the address bar never shows a half-written state. A null
 * `teamId` detaches the whole grammar; a null `mode` (view, edit, copy — the
 * modes the UI never writes) drops the `mode` param and leaves `viewTeam`.
 */
export function setTeamModalUrl(input: {
  teamId: string | null;
  mode: TeamModalUrlMode | null;
}): void {
  const params = new URLSearchParams(window.location.search);

  if (input.teamId == null) {
    params.delete(HomeQueryParam.ViewTeam);
    params.delete(HomeQueryParam.TeamModalMode);
  } else {
    params.set(HomeQueryParam.ViewTeam, input.teamId);
    if (input.mode === TeamModalUrlMode.Deploy) {
      params.set(HomeQueryParam.TeamModalMode, input.mode);
    } else {
      params.delete(HomeQueryParam.TeamModalMode);
    }
  }

  const nextUrl = params.toString()
    ? `${window.location.pathname}?${params.toString()}`
    : window.location.pathname;
  window.history.replaceState(null, "", nextUrl);
}

/**
 * Attach `?viewTeam=` so a FULL PAGE LOAD reopens this team — the landing's
 * `useViewTeamUrlRestore` is the only thing that can put the team modal back
 * after a redirecting sign-in, and the standalone team overlay does not attach
 * the param on its own.
 *
 * Never clobbers an existing attachment: the gallery deck writes this param
 * from its own committed-open bookkeeping, and silently retargeting it would
 * desynchronise that writer. Returns true only when THIS call attached it, so
 * the caller can take back exactly what it added.
 */
export function attachViewTeamUrl(teamId: string): boolean {
  if (getViewTeamUrl() != null) return false;
  setViewTeamUrl(teamId);
  return true;
}

/** Detach `?viewTeam=` only while it still names this team. */
export function detachViewTeamUrl(teamId: string): void {
  if (getViewTeamUrl() !== teamId) return;
  setViewTeamUrl(null);
}
